import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CustomArrowProps,
  Settings,
  default as ReactSlickCarousel,
  ResponsiveObject,
} from 'react-slick';
import {
  MdKeyboardArrowLeft as LeftArrowIcon,
  MdKeyboardArrowRight as RightArrowIcon,
} from 'react-icons/md';
import './carousel.styles.scss';
import { trackEvent } from '@/utils/analytics';
import type { CarouselPlacement, CarouselSlideMethod } from '@/types/analytics-events';

interface Props {
  children: React.ReactNode;
  settings?: Settings;
  // When set, slide changes made by the visitor are sent to GA as `carousel_slide`
  trackingPlacement?: CarouselPlacement;
}

interface ArrowProps extends CustomArrowProps {
  title: string;
  icon: React.ReactNode;
  onArrowClick: () => void;
}

const Arrow = ({ className, onClick, title, icon, onArrowClick }: ArrowProps) => {
  return (
    <button
      className={className + ' carousel__arrow'}
      onClick={(e) => {
        onArrowClick();
        onClick?.(e);
      }}
      title={title}
    >
      {icon}
    </button>
  );
};

const Carousel: React.FC<Props> = ({ children, settings, trackingPlacement }) => {
  const getSlidesToShow = useCallback(() => {
    if (typeof window === 'undefined') return (settings && (settings.slidesToShow as number)) ?? 3;
    const width = window.innerWidth;

    const breakpointSettings = (settings && (settings.responsive as ResponsiveObject[])) || [];
    const breakpointSettingsAscending = breakpointSettings.sort(
      (a, b) => a.breakpoint - b.breakpoint,
    );

    for (const bps of breakpointSettingsAscending) {
      if (width <= bps.breakpoint && bps.settings !== 'unslick') {
        return bps.settings.slidesToShow as number;
      }
    }

    // Fallback to provided default or 3
    return (settings && (settings.slidesToShow as number)) ?? 3;
  }, [settings]);

  const [slidesToShow, setSlidesToShow] = useState<number>(getSlidesToShow());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setSlidesToShow(getSlidesToShow());
    window.addEventListener('resize', handleResize);
    // run on mount
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [getSlidesToShow]);

  // Set by an arrow click or swipe; react-slick then calls beforeChange synchronously, so changes
  // without one (e.g. a resize or keyboard) aren't tracked.
  const pendingMethod = useRef<CarouselSlideMethod | null>(null);
  const markUserMove = (method: CarouselSlideMethod) => {
    pendingMethod.current = method;
    queueMicrotask(() => (pendingMethod.current = null));
  };

  const handleBeforeChange = (currentSlide: number, nextSlide: number) => {
    settings?.beforeChange?.(currentSlide, nextSlide);
    const method = pendingMethod.current;
    pendingMethod.current = null;
    if (trackingPlacement && method && currentSlide !== nextSlide) {
      trackEvent('carousel_slide', { placement: trackingPlacement, method });
    }
  };

  const appliedSettings: Settings = {
    ...(settings || {}),
    slidesToShow,
    slidesToScroll: slidesToShow,
  };

  return (
    <ReactSlickCarousel
      {...appliedSettings}
      infinite={false}
      beforeChange={handleBeforeChange}
      onSwipe={(direction) => {
        settings?.onSwipe?.(direction);
        markUserMove('swipe');
      }}
      nextArrow={
        <Arrow title="next" icon={<RightArrowIcon />} onArrowClick={() => markUserMove('arrow')} />
      }
      prevArrow={
        <Arrow
          title="previous"
          icon={<LeftArrowIcon />}
          onArrowClick={() => markUserMove('arrow')}
        />
      }
      className="carousel"
    >
      {children}
    </ReactSlickCarousel>
  );
};

export default Carousel;
